import React, { Component } from 'react';
import { bool, func, object, node, number, shape, string, arrayOf } from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { createResourceLocatorString } from '../../../util/routes';

import { ModalInMobile, Button } from '../../../components';

import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';
import SelectedPills from '../MainPanelHeader/SelectedPills';
import css from './SearchFiltersMobile.module.css';

class SearchFiltersMobileComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isFiltersOpenOnMobile: false, initialQueryParams: null };

    this.openFilters = this.openFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
  }

  // Open filters modal, set the initial parameters to current ones
  openFilters() {
    const { onOpenModal, urlQueryParams } = this.props;
    onOpenModal();
    this.setState({ isFiltersOpenOnMobile: true, initialQueryParams: urlQueryParams });
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    const { history, onCloseModal, routeConfiguration } = this.props;

    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        this.state.initialQueryParams
      )
    );
    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false, initialQueryParams: null });
  }

  // Close the filter modal
  closeFilters() {
    this.props.onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    this.props.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      rootClassName,
      className,
      children,
      sortByComponent,
      viewToggleComponent,
      listingsAreLoaded,
      resultsCount,
      searchInProgress,
      showAsModalMaxWidth,
      onMapIconClick,
      onManageDisableScrolling,
      selectedFiltersCount,
      noResultsInfo,
      intl,
      isMapVariant,
      pills,
      onRemovePill,
      hideResultCount,
      resultsNoun,
      saveSearchComponent,
      onClearPills,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const resultsFound = (
      <FormattedMessage id="SearchFiltersMobile.foundResults" values={{ count: resultsCount }} />
    );

    const noResults = <FormattedMessage id="SearchFiltersMobile.noResults" />;
    const loadingResults = <FormattedMessage id="SearchFiltersMobile.loadingResults" />;
    const filtersHeading = intl.formatMessage({ id: 'SearchFiltersMobile.heading' });
    const modalCloseButtonMessage = intl.formatMessage({ id: 'SearchFiltersMobile.cancel' });

    /* P25009-432 BR-2 — the sticky apply button names what it does:
     * "Visa 246 resultat". While a search is in flight the count is
     * stale, so fall back to the loading label. `showResults` is a NEW
     * key on purpose: the old `showListings` has a hosted Console
     * override ("Visa X objekt") that would mask any en.json change. */
    /* P25009-486 BR-2 — "Visa 5 hjul": the product in view, not "resultat".
     * Another new key, for the same Console-override reason. */
    const showListingsLabel = searchInProgress
      ? intl.formatMessage({ id: 'SearchFiltersMobile.loadingResults' })
      : resultsNoun
        ? intl.formatMessage(
            { id: 'SearchFiltersMobile.showProducts' },
            { count: resultsCount ?? 0, noun: resultsNoun }
          )
        : intl.formatMessage({ id: 'SearchFiltersMobile.showResults' }, { count: resultsCount ?? 0 });

    const hasNoResults = listingsAreLoaded && resultsCount === 0;

    return (
      <div className={classes}>
        {/* The phone's results toolbar, one row (P25009-491 BR-5): "N
          * resultat" — the page heading (P25009-432 BR-1) — then Filter (n),
          * the sort dropdown and Bevaka. The real filter pills and "Rensa"
          * go on the row under it; the car and the product are in the car
          * bar, never pills. */}
        <div className={css.toolbar}>
          {!hideResultCount ? (
            <h2 className={css.searchResultSummary}>
              {searchInProgress
                ? loadingResults
                : listingsAreLoaded && resultsCount > 0
                  ? resultsFound
                  : hasNoResults
                    ? noResults
                    : null}
            </h2>
          ) : null}

          <PopupOpenerButton isSelected={selectedFiltersCount > 0} toggleOpen={this.openFilters}>
            {/* BR-2 — the Filter button carries the active-filter count. */}
            {selectedFiltersCount > 0 ? (
              <FormattedMessage
                id="SearchFiltersMobile.filtersButtonLabelWithCount"
                values={{ count: selectedFiltersCount }}
              />
            ) : (
              <FormattedMessage
                id="SearchFiltersMobile.filtersButtonLabel"
                className={css.mapIconText}
              />
            )}
          </PopupOpenerButton>

          {!hasNoResults ? sortByComponent : null}
          {saveSearchComponent}
          {isMapVariant ? (
            <div className={css.mapIcon} onClick={onMapIconClick}>
              <FormattedMessage id="SearchFiltersMobile.openMapView" className={css.mapIconText} />
            </div>
          ) : null}
        </div>

        {pills?.length || viewToggleComponent ? (
          <div className={css.pillsRow}>
            {pills?.length ? (
              <div className={css.mobilePillsWrapper}>
                <SelectedPills pills={pills} onRemove={onRemovePill} />
              </div>
            ) : null}
            {pills?.length && onClearPills ? (
              <button type="button" className={css.clearPills} onClick={onClearPills}>
                Rensa
              </button>
            ) : null}
            {viewToggleComponent ? (
              <div className={css.viewToggleWrap}>{viewToggleComponent}</div>
            ) : null}
          </div>
        ) : null}

        {noResultsInfo ? noResultsInfo : null}

        <ModalInMobile
          id="SearchFiltersMobile.filters"
          className={css.sheetModal}
          isModalOpenOnMobile={this.state.isFiltersOpenOnMobile}
          onClose={this.cancelFilters}
          showAsModalMaxWidth={showAsModalMaxWidth}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          closeButtonMessage={modalCloseButtonMessage}
        >
          <div className={css.modalHeadingWrapper}>
            <span className={css.modalHeading}>{filtersHeading}</span>
            <button className={css.resetAllButton} onClick={e => this.resetAll(e)}>
              <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
            </button>
          </div>
          {this.state.isFiltersOpenOnMobile ? (
            <div className={css.filtersWrapper}>{children}</div>
          ) : null}

          <div className={css.showListingsContainer}>
            <Button className={css.showListingsButton} onClick={this.closeFilters}>
              {showListingsLabel}
            </Button>
          </div>
        </ModalInMobile>
      </div>
    );
  }
}

SearchFiltersMobileComponent.defaultProps = {
  rootClassName: null,
  className: null,
  sortByComponent: null,
  resultsCount: null,
  searchInProgress: false,
  selectedFiltersCount: 0,
  isMapVariant: true,
  onMapIconClick: () => { },
};

SearchFiltersMobileComponent.propTypes = {
  rootClassName: string,
  className: string,
  urlQueryParams: object.isRequired,
  sortByComponent: node,
  listingsAreLoaded: bool.isRequired,
  resultsCount: number,
  searchInProgress: bool,
  showAsModalMaxWidth: number.isRequired,
  onMapIconClick: func,
  onManageDisableScrolling: func.isRequired,
  onOpenModal: func.isRequired,
  onCloseModal: func.isRequired,
  resetAll: func.isRequired,
  selectedFiltersCount: number,
  isMapVariant: bool,

  // from useIntl
  intl: intlShape.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

const SearchFiltersMobile = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();

  return (
    <SearchFiltersMobileComponent
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      {...props}
    />
  );
};

export default SearchFiltersMobile;
